import React, { useState } from 'react';
import { api } from '../../contexts/AuthContext';
import { toast } from 'sonner';
import { Loader2, UserPlus, CalendarPlus, CheckCircle2 } from 'lucide-react';

const fmtDate = (d) => new Date(d + 'T12:00:00').toLocaleDateString([], { weekday: 'short', month: 'short', day: 'numeric' });

export const GapsPanel = ({ data, reload }) => {
  const { gaps, line_labels } = data;
  const [busy, setBusy] = useState('');
  const [asDraft, setAsDraft] = useState(true);

  const fill = async (gap, sug) => {
    const key = `${gap.shift_id || gap.participant}-${gap.date}-${sug.employee_id}`;
    setBusy(key);
    try {
      if (gap.kind === 'unassigned_shift') {
        await api.put(`/shifts/${gap.shift_id}`, { employee_id: sug.employee_id });
        toast.success(`${sug.name} assigned to the open shift on ${fmtDate(gap.date)}`);
      } else {
        await api.post('/shifts', {
          employee_id: sug.employee_id, service_line: gap.service_line, date: gap.date,
          time_in: gap.time_in, time_out: gap.time_out, location: gap.location || '',
          participants: [gap.participant], is_draft: asDraft,
        });
        toast.success(`${asDraft ? 'Draft shift' : 'Shift'} created: ${sug.name} → ${gap.participant} on ${fmtDate(gap.date)}`);
      }
      reload();
    } catch (e) {
      const det = e.response?.data?.detail;
      toast.error(typeof det === 'string' ? det : det?.message || 'Failed to fill gap');
    }
    setBusy('');
  };

  return (
    <div className="space-y-3" data-testid="gaps-panel">
      <div className="flex items-center justify-between">
        <p className="text-xs text-white/40">Every gap below is unstaffed. Suggestions are ranked by open capacity + availability.</p>
        <label className="flex items-center gap-2 text-xs font-semibold text-white/60 cursor-pointer shrink-0">
          <input type="checkbox" checked={asDraft} onChange={e => setAsDraft(e.target.checked)} className="accent-[#FFD66B]" data-testid="gaps-draft-toggle" />
          Create new shifts as drafts
        </label>
      </div>
      {gaps.length === 0 && (
        <div className="text-center py-16 text-white/40">
          <CheckCircle2 className="h-10 w-10 mx-auto mb-2 text-[#34C98E]" />
          <p className="text-sm font-semibold text-white/70">Fully covered</p>
          <p className="text-xs">No open shifts or unmet participant schedule blocks this week.</p>
        </div>
      )}
      {gaps.map((g, gi) => (
        <div key={gi} className="bg-white/[0.03] border border-white/10 rounded-2xl p-4" data-testid={`gap-${gi}`}>
          <div className="flex flex-wrap items-center gap-2 mb-2.5">
            <span className={`text-[9px] font-bold uppercase px-2 py-0.5 rounded-full ${g.kind === 'unassigned_shift' ? 'bg-[#3A2F10] text-[#FFD66B]' : 'bg-[#12233F] text-[#7EA9F5]'}`}>
              {g.kind === 'unassigned_shift' ? 'Open shift' : 'Participant unmet'}
            </span>
            <span className="text-sm font-bold">
              {g.kind === 'template_gap' ? `${g.participant} · ` : ''}{fmtDate(g.date)} · {g.time_in}–{g.time_out}
            </span>
            <span className="text-xs text-white/40">{line_labels[g.service_line] || g.service_line}{g.location ? ` · ${g.location}` : ''}{g.group_number ? ` · Group ${g.group_number}` : ''}</span>
            {g.participants?.length > 0 && <span className="text-xs text-white/40">w/ {g.participants.join(', ')}</span>}
          </div>
          {g.suggestions.length === 0 ? (
            <p className="text-xs text-[#FF7BA9]">No staff matches this block (service line, availability, or time conflicts). Consider adjusting the block.</p>
          ) : (
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-2">
              {g.suggestions.map(s => {
                const key = `${g.shift_id || g.participant}-${g.date}-${s.employee_id}`;
                return (
                  <button key={s.employee_id} onClick={() => fill(g, s)} disabled={!!busy}
                          className={`text-left rounded-xl border p-2.5 transition-colors ${s.would_exceed ? 'border-[#FFD66B]/30 bg-[#3A2F10]/30 hover:bg-[#3A2F10]/60' : 'border-[#34C98E]/30 bg-[#0E2B21]/30 hover:bg-[#0E2B21]/70'}`}
                          data-testid={`gap-${gi}-suggest-${s.employee_id}`}>
                    <div className="flex items-center gap-1.5 text-sm font-bold">
                      {busy === key ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : (g.kind === 'unassigned_shift' ? <UserPlus className="h-3.5 w-3.5 text-[#34C98E]" /> : <CalendarPlus className="h-3.5 w-3.5 text-[#7EA9F5]" />)}
                      {s.name}
                    </div>
                    {s.reasons.map((r, ri) => <div key={ri} className="text-[10px] text-white/45 leading-snug">{r}</div>)}
                  </button>
                );
              })}
            </div>
          )}
        </div>
      ))}
    </div>
  );
};

export default GapsPanel;
